/**
 * SchemaRefreshButton — the ⟳ Schema control shared by every SQL editor.
 *
 * Reflects the per-connection autocomplete cache state: spins ("Schema…") while
 * the background populate or a refresh runs, lights up green ("Schema ✓") once
 * the schema is loaded and current. Click refreshes the given database;
 * Shift-click refreshes the whole connection.
 *
 * Props (each may be a plain value or a reactive accessor):
 *   connId    — connection id
 *   database  — current database (refresh target for a plain click)
 */
import { createSignal } from 'solid-js';
import { refreshCompletionSchema, completionState } from '../lib/schema.js';

const read = (v) => (typeof v === 'function' ? v() : v);

export default function SchemaRefreshButton(props) {
  const connId = () => read(props.connId);
  const database = () => read(props.database);
  const [refreshing, setRefreshing] = createSignal(false);

  const building = () => { const id = connId(); return id ? completionState(id)().building : false; };
  const busy = () => building() || refreshing();
  const ready = () => {
    const id = connId();
    if (!id) return false;
    const st = completionState(id)();
    return st.loaded && !busy() && (st.databases || []).length > 0;
  };

  const refresh = async (all) => {
    const id = connId();
    if (!id) return;
    setRefreshing(true);
    try { await refreshCompletionSchema(id, all ? { all: true } : { database: database() }); }
    finally { setRefreshing(false); }
  };

  return (
    <button
      class={`btn btn-sm ${ready() ? 'schema-ready' : 'btn-ghost'}`}
      onClick={(e) => refresh(e.shiftKey)}
      disabled={busy()}
      title={busy()
        ? 'Building the autocomplete schema cache…'
        : ready()
          ? `Autocomplete schema is up to date. Click to refresh ${database() || 'this database'} (Shift-click: all databases).`
          : `Refresh autocomplete schema for ${database() || 'this database'} (Shift-click: all databases). Use this if the schema changed outside Ninocat.`}>
      <span class={busy() ? 'schema-spin' : ''}>⟳</span> Schema{busy() ? '…' : ready() ? ' ✓' : ''}
    </button>
  );
}
